import { CircularProgress } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import DefaultPricingCard from "@/components/atoms/Card/PricingCards/DefaultPricingCard";
import Suspense from "@/components/molecules/Suspense";
import { SUBSCRIBE_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import useSalesProductPriceData from "@/hooks/data/use-sales-product-price-data";
import useGetAllCustomerSalesProducts from "@/hooks/requests/customer-sales-product/use-get-all-customer-sales-product";
import Iplan from "@/interfaces/sales-product-price.interfaces";
import { PlanMessages } from "@/messages/plans/plan.messages";

import { pageNumber, pageSize } from "../../constants";

type Props = {
  messages: PlanMessages;
  plan: Iplan;
};

const PlanCard = ({ messages, plan }: Props) => {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { showToast } = useToast();
  const { checkPermissions } = useAuth();
  const { data, isLoading } = useGetAllCustomerSalesProducts({ pageNumber, pageSize });

  const hasNoPermissionToCreate = checkPermissions(SUBSCRIBE_PERMISSIONS.CREATE);

  const isPlanContracted = data?.items.some(
    (cusPlan) => cusPlan.salesProductPrice?.id === plan.id && cusPlan.status === "A"
  );

  const handleSubscribe = () => {
    if (hasNoPermissionToCreate) {
      showToast({ text: messages.permissionErrorMessage, type: "error" });
      return;
    }
    router(ROUTE_URLS.subscribe(lang, plan.id));
  };

  const additionalFees = {
    totalPerEventValue: plan?.totalPerEventValue as number | undefined,
    totalFeePerSalesConvertionValue: plan?.totalFeePerSalesConvertionValue as number | undefined,
    totalSalesConvertionValue: plan?.totalSalesConvertionValue as number | undefined,
  };

  const salesProductPrice = plan;
  const salesProductPriceData = useSalesProductPriceData({ salesProductPrice, additionalFees });

  return (
    <Suspense isSuspended={isLoading} fallback={<CircularProgress />}>
      <DefaultPricingCard
        color={plan.planContrast ? "dark" : "white"}
        badge={{
          color: plan.planContrast ? "primary" : "light",
          label: plan.planName,
        }}
        price={{
          currency: messages.planFeeCurrency,
          value: plan.fixedMonthlyFee.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          type: messages.planFeeMonth,
        }}
        specifications={[
          {
            label: messages.feePerOrderText,
            includes: false,
            focus: true,
            format: salesProductPriceData.formattedFeePerSalesConvertionWithPlus,
          },
          {
            label: messages.feePerConversationText,
            includes: false,
            focus: true,
            format: salesProductPriceData.formattedPricePerEvent,
          },
          {
            label: plan.planDescription || "",
            includes: false,
          },
        ]}
        isPlanContracted={isPlanContracted}
        action={{
          type: "internal",
          route: "",
          label: isPlanContracted ? messages.contractedButton : messages.buyNowButton,
          color: plan.planContrast ? "light" : "dark",
          variant: plan.planContrast ? "outlined" : "gradient",
          onClick: handleSubscribe,
        }}
      />
    </Suspense>
  );
};

export default PlanCard;
